import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1a1f2e;
  margin: 0;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #1a1f2e;
  }
`;

const Body = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1a1f2e;
  transition: all 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #2DBCAF;
    box-shadow: 0 0 0 3px rgba(45, 188, 175, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1a1f2e;
  transition: all 0.2s;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2DBCAF;
    box-shadow: 0 0 0 3px rgba(45, 188, 175, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f1f5f9;
  border-radius: 8px;
  font-size: 13px;
  color: #64748b;
`;

const Footer = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${p => p.$primary ? `
    background: #2DBCAF;
    border: none;
    color: #fff;
    
    &:hover {
      background: #25a89c;
    }
    
    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  ` : `
    background: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    
    &:hover {
      border-color: #cbd5e1;
      color: #475569;
    }
  `}
`;

const ErrorMsg = styled.div`
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #dc2626;
  font-size: 13px;
  margin-bottom: 20px;
`;

const SuccessMsg = styled.div`
  padding: 12px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  color: #16a34a;
  font-size: 13px;
  margin-bottom: 20px;
`;

interface StimulasiItem {
  _id: string;
  featureSlug: string;
  ageGroup?: string;
  category?: string;
  title: string;
  description?: string;
  metadata?: {
    week?: number;
    day?: number;
    tips?: string;
  };
  isActive: boolean;
}

interface EditStimulasiModalProps {
  item: StimulasiItem;
  onClose: () => void;
  onSaved: (updatedItem: StimulasiItem) => void;
}

const ageGroupLabels: Record<string, string> = {
  '0-3m': '0-3 bulan',
  '4-6m': '4-6 bulan',
  '7-9m': '7-9 bulan',
  '10-12m': '10-12 bulan',
  '1-2y': '1-2 tahun',
  '2-3y': '2-3 tahun',
  '3-4y': '3-4 tahun',
  '4-5y': '4-5 tahun',
  '5-6y': '5-6 tahun',
};

const categoryLabels: Record<string, string> = {
  motorikKasar: 'Motorik Kasar',
  motorikHalus: 'Motorik Halus',
  bicara: 'Bicara',
  sosial: 'Sosial',
};

const EditStimulasiModal: React.FC<EditStimulasiModalProps> = ({ item, onClose, onSaved }) => {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description || '');
  const [tips, setTips] = useState(item.metadata?.tips || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Judul tidak boleh kosong');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const res = await fetch(`/api/admin/tumbuh-kembang/content/${item._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          metadata: {
            ...item.metadata,
            tips: tips.trim(),
          },
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Gagal menyimpan');
      }

      const data = await res.json();
      setSuccess(true);
      
      setTimeout(() => {
        onSaved({
          ...item,
          title: title.trim(),
          description: description.trim(),
          metadata: {
            ...item.metadata,
            tips: tips.trim(),
          },
        });
      }, 500);
    } catch (err: any) {
      setError(err.message || 'Terjadi kesalahan');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal>
        <Header>
          <ModalTitle>Edit Stimulasi</ModalTitle>
          <CloseBtn onClick={onClose}>×</CloseBtn>
        </Header>
        
        <Body>
          <MetaInfo>
            {item.ageGroup && (
              <MetaBadge>
                📅 {ageGroupLabels[item.ageGroup] || item.ageGroup}
              </MetaBadge>
            )}
            {item.category && (
              <MetaBadge>
                📂 {categoryLabels[item.category] || item.category}
              </MetaBadge>
            )}
          </MetaInfo>

          {error && <ErrorMsg>{error}</ErrorMsg>}
          {success && <SuccessMsg>✓ Berhasil disimpan!</SuccessMsg>}

          <FormGroup>
            <Label>Judul</Label>
            <Input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Masukkan judul stimulasi"
            />
          </FormGroup>

          <FormGroup>
            <Label>Deskripsi</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Masukkan deskripsi stimulasi"
              rows={4}
            />
          </FormGroup>

          <FormGroup>
            <Label>Tips</Label>
            <Textarea
              value={tips}
              onChange={(e) => setTips(e.target.value)}
              placeholder="Masukkan tips untuk orang tua"
              rows={3}
            />
          </FormGroup>
        </Body>

        <Footer>
          <Button onClick={onClose}>Batal</Button>
          <Button $primary onClick={handleSave} disabled={saving}>
            {saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default EditStimulasiModal;
